import { useRef } from "react";
import { Box, MenuItem } from "@mui/material";
import { IInvestmentUniverseGroup } from "src/api/server/wf-detail.types";
import { IconCaret } from "src/components/icons-mui/caret";
import { useContextMenu } from "src/components-mui/base/context-menu/use-context-menu";
import { useFlipPlacement } from "src/components-mui/base/context-menu/use-flip-placement";
import { Typography } from "src/components-mui/base/typography/typography";
import { ContextMenu } from "src/components-mui/common/context-menu/context-menu";

const MENU_MAX_HEIGHT = 250;
const MENU_ANCHOR_GAP = 1;

type TInvestmentUniverseGroupProps = IInvestmentUniverseGroup;

export const InvestmentUniverseGroup = ({ universeGroupId, universeGroupName, universes }: TInvestmentUniverseGroupProps) => {
    const { anchorRef, isOpen, onOpen, onClose } = useContextMenu();
    const menuId = useRef(`investment-universe-menu-${universeGroupId}`).current;
    const buttonId = `${menuId}-button`;
    const isCrossedOut = universes.every(universe => universe.isCrossedOut);
    const availableCount = universes.length;
    const assignedCount = universes.filter(universe => !universe.isCrossedOut).length;
    const { updateFlipped, anchorOrigin, transformOrigin, anchorGap } = useFlipPlacement({
        anchorRef,
        isOpen,
        menuMaxHeight: MENU_MAX_HEIGHT,
        anchorGap: MENU_ANCHOR_GAP,
    });

    const handleButtonClick = () => {
        if (isOpen) {
            onClose();
            return;
        }
        updateFlipped();
        onOpen();
    };

    return (
        <>
            <Box
                component="button"
                type="button"
                ref={anchorRef}
                id={buttonId}
                aria-haspopup="menu"
                aria-controls={isOpen ? menuId : undefined}
                aria-expanded={isOpen ? "true" : undefined}
                onClick={handleButtonClick}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px solid",
                    borderColor: isCrossedOut ? "gray.50" : "gray.800",
                    backgroundColor: isCrossedOut ? "gray.50" : "transparent",
                    color: isCrossedOut ? "gray.200" : "inherit",
                    borderRadius: "16px",
                    py: 1,
                    px: 2,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "gray.50" },
                }}
            >
                <Typography
                    sx={{
                        fontSize: "sm",
                        lineHeight: "16px",
                        fontWeight: "bold",
                        mr: 0.5,
                        textDecoration: isCrossedOut ? "line-through" : undefined,
                    }}
                >
                    {universeGroupName}
                </Typography>
                <Typography
                    sx={{
                        fontSize: "2xs",
                        lineHeight: "normal",
                        fontWeight: "semibold",
                        mr: 1,
                        borderRadius: "16px",
                        px: 1,
                        py: "2px",
                        backgroundColor: "gray.500",
                        color: "white",
                    }}
                >
                    {assignedCount} / {availableCount}
                </Typography>
                <IconCaret sx={{ width: 16, height: 16, transition: "transform 0.3s", transform: isOpen ? "rotate(180deg)" : undefined }} />
            </Box>
            <ContextMenu
                id={menuId}
                open={isOpen}
                anchorRef={anchorRef}
                onClose={onClose}
                anchorGap={anchorGap}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                marginThreshold={null}
                zIndex={1}
                sx={{ "& .MuiMenu-paper": { minWidth: 180 } }}
                slotProps={{ list: { "aria-labelledby": buttonId }, paper: { sx: { maxHeight: MENU_MAX_HEIGHT } } }}
            >
                {universes.map(({ universeId, name, isCrossedOut: isUniverseCrossedOut }) => (
                    <MenuItem
                        key={universeId}
                        sx={{
                            py: 1,
                            height: "auto",
                            minHeight: { xs: 0, sm: 0 },
                            pointerEvents: "none",
                            "&:hover, &.Mui-focusVisible": { backgroundColor: "common.white" },
                            fontSize: "sm",
                            ...(isUniverseCrossedOut && { color: "gray.200", textDecoration: "line-through" }),
                        }}
                    >
                        {name}
                    </MenuItem>
                ))}
            </ContextMenu>
        </>
    );
};
